import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Schema } from '@pkg/zero'
import { useQuery, useZero } from '@rocicorp/zero/react'
import type { DayObjectSearchEntry } from 'types/graphql'

import { useAuth } from 'src/auth'
import { logger as loggerDev } from 'src/lib/logger'
import { globalWorkspaceId, type NativeObjectType } from 'src/lib/objects'

import { buildObject } from './build'
import ObjectContext from './ObjectContext'
import { getDayObjectSearchEntry } from './searchIndex'
import useObjects from './useObjects'

const loggingEnabled = false

const logger = loggingEnabled
  ? loggerDev
  : {
      dev: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
    }

const ObjectProvider = ({
  workspaceId,
  objectId,
  objectType,
  children,
}: {
  workspaceId: string
  objectId: string
  objectType: NativeObjectType
  children: React.ReactNode
}) => {
  const { currentUser } = useAuth()
  const { propertyDefinitions } = useObjects()
  const [searchEntry, setSearchEntry] = useState<DayObjectSearchEntry | null>(
    null
  )

  const initializedRef = useRef(false)

  const z = useZero<Schema>()

  const [objectRowsResult] = useQuery(
    z.query.objectProperty.where(({ and, cmp }) =>
      and(
        cmp('workspaceId', 'IN', [globalWorkspaceId, workspaceId]),
        cmp('objectTypeId', objectType),
        cmp('objectId', objectId),
        cmp('userId', 'IN', [currentUser?.id, ''])
      )
    )
  )

  const object = useMemo(() => {
    if (!Array.isArray(objectRowsResult)) {
      return null
    }

    return buildObject({
      objectType,
      objectId,
      workspaceId,
      rows: objectRowsResult as any[],
      propertyDefinitions: Object.values(propertyDefinitions[objectType]),
    })
  }, [objectRowsResult, objectId, objectType, propertyDefinitions, workspaceId])

  logger.dev('ObjectProvider', {
    object,
    objectRowsResult,
    objectId,
    objectType,
    workspaceId,
  })

  useEffect(() => {
    if (initializedRef.current) return
    if (!workspaceId) return

    const initHandler = async () => {
      const searchEntry = await getDayObjectSearchEntry({
        workspaceId,
        objectType,
        objectId,
      })
      setSearchEntry(searchEntry)

      initializedRef.current = true
    }

    initHandler()
  }, [workspaceId, objectId, objectType])

  const handleUpdateProperty = useCallback(
    async ({
      objectId,
      objectType,
      propertyId,
      propertyValue,
    }: {
      objectId: string
      objectType: string
      propertyId: string
      propertyValue: any
    }) => {
      //setSearchEntry(null)

      logger.dev('handleUpdateObject', {
        objectId,
        objectType,
        propertyId,
        propertyValue,
      })
    },
    []
  )

  const value = useMemo(
    () => ({
      workspaceId,
      searchEntry,
      updateProperty: handleUpdateProperty,
      object,
    }),
    [workspaceId, searchEntry, handleUpdateProperty, object]
  )
  return (
    <ObjectContext.Provider value={value}>{children}</ObjectContext.Provider>
  )
}

export default ObjectProvider
